import { Amplify } from 'aws-amplify'
import { 
  signIn, 
  signUp, 
  signOut, 
  confirmSignUp, 
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchUserAttributes,
  updateUserAttributes,
  signInWithRedirect,
  AuthError
} from 'aws-amplify/auth'
// import { CognitoIdentityProviderClient } from '@aws-sdk/client-cognito-identity-provider'
import { cognitoConfig, isFeatureEnabled } from '../config/app'

// Cognito client for custom flows (if needed)
// const cognitoClient = new CognitoIdentityProviderClient({
//   region: cognitoConfig.region
// })

// Track initialization state
let isCognitoInitialized = false

// Initialize Amplify with Cognito configuration
export const initializeCognito = () => {
  if (!isFeatureEnabled('enableCognito')) {
    return
  }

  try {
    const amplifyConfig: any = {
      Auth: {
        Cognito: {
          userPoolId: cognitoConfig.userPoolId,
          userPoolClientId: cognitoConfig.userPoolWebClientId,
          loginWith: {
            oauth: {
              domain: cognitoConfig.hostedUIDomain,
              scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
              redirectSignIn: [window.location.origin + '/'],
              redirectSignOut: [window.location.origin + '/'],
              responseType: 'code'
            }
          }
        }
      }
    }

    Amplify.configure(amplifyConfig)
    isCognitoInitialized = true
  } catch (error) {
    throw error
  }
}

// Check if Cognito is initialized
export const isCognitoReady = () => isCognitoInitialized

// Authentication types
export interface SignUpParams {
  username: string
  password: string
  email: string
  phoneNumber?: string
  givenName?: string
  familyName?: string
}

export interface SignInParams {
  username: string
  password: string
}

export interface ConfirmSignUpParams {
  username: string
  confirmationCode: string
}

export interface ResetPasswordParams {
  username: string
}

export interface ConfirmResetPasswordParams {
  username: string
  confirmationCode: string
  newPassword: string
}

export interface UserAttributes {
  email?: string
  phoneNumber?: string
  givenName?: string
  familyName?: string
  [key: string]: string | undefined
}

// Custom authentication flow types
export interface OTPSignUpParams {
  username: string // email or phone number
}

export interface OTPConfirmSignUpParams {
  generatedUsername: string
  confirmationCode: string
  session: string
}

export interface SRPSignInParams {
  username: string
  password: string
}

export interface RefreshTokenParams {
  username: string
  refreshToken: string
}

export interface CustomAuthParams {
  username: string // email or phone number
}

export interface CustomAuthChallengeParams {
  session: string
  deliveryMedium: 'email' | 'phone_number'
  username: string
}

export interface CustomAuthOTPParams {
  session: string
  otpCode: string
  username: string
}

export interface AuthFlowResult {
  success: boolean
  data?: any
  error?: string
  session?: string
  challengeName?: string
  challengeParameters?: any
}

// Authentication service functions
export const authService = {
  // Sign up a new user
  async signUp({ username, password, email, phoneNumber, givenName, familyName }: SignUpParams) {
    try {
      const result = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
            phone_number: phoneNumber,
            given_name: givenName,
            family_name: familyName,
          }
        }
      })
      return { success: true, data: result }
    } catch (error) {
      return { success: false, error: error as AuthError }
    }
  },

  // Sign in user
  async signIn({ username, password }: SignInParams) {
    try {
      const result = await signIn({
        username,
        password
      })
      return { success: true, data: result }
    } catch (error) {
      return { success: false, error: error as AuthError }
    }
  },

  // Sign out user
  async signOut() {
    try {
      await signOut()
      return { success: true }
    } catch (error) {
      return { success: false, error: error as AuthError }
    }
  },

  // Confirm sign up with verification code
  async confirmSignUp({ username, confirmationCode }: ConfirmSignUpParams) {
    try {
      const result = await confirmSignUp({
        username,
        confirmationCode
      })
      return { success: true, data: result }
    } catch (error) {
      return { success: false, error: error as AuthError }
    }
  },

  // Resend sign up verification code
  async resendSignUpCode(username: string) {
    try {
      const result = await resendSignUpCode({ username })
      return { success: true, data: result }
    } catch (error) {
      return { success: false, error: error as AuthError }
    }
  },

  // Reset password
  async resetPassword({ username }: ResetPasswordParams) {
    try {
      const result = await resetPassword({ username })
      return { success: true, data: result }
    } catch (error) {
      return { success: false, error: error as AuthError }
    }
  },

  // Confirm password reset
  async confirmResetPassword({ username, confirmationCode, newPassword }: ConfirmResetPasswordParams) {
    try {
      const result = await confirmResetPassword({
        username,
        confirmationCode,
        newPassword
      })
      return { success: true, data: result }
    } catch (error) {
      return { success: false, error: error as AuthError }
    }
  },

  // Get current authenticated user
  async getCurrentUser() {
    try {
      const user = await getCurrentUser()
      return { success: true, data: user }
    } catch (error) {
      // Don't log UserUnAuthenticatedException as it's expected when no user is logged in
      return { success: false, error: error as AuthError }
    }
  },

  // Fetch user attributes
  async fetchUserAttributes() {
    try {
      const attributes = await fetchUserAttributes()
      return { success: true, data: attributes }
    } catch (error) {
      // Don't log UserUnAuthenticatedException as it's expected when no user is logged in
      return { success: false, error: error as AuthError }
    }
  },

  // Update user attributes
  async updateUserAttributes(attributes: UserAttributes) {
    try {
      const result = await updateUserAttributes({ userAttributes: attributes })
      return { success: true, data: result }
    } catch (error) {
      return { success: false, error: error as AuthError }
    }
  },

  // Change password
  async changePassword(oldPassword: string, newPassword: string) {
    try {
      // In AWS Amplify v6, changePassword is not directly available
      // This would need to be implemented using the Admin API or a custom Lambda function
      // For now, we'll throw an error indicating this needs to be implemented
      throw new Error('Change password functionality requires custom implementation in AWS Amplify v6')
    } catch (error) {
      return { success: false, error: error as AuthError }
    }
  },

  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    try {
      await getCurrentUser()
      return true
    } catch {
      return false
    }
  },

  // Sign in with Google OAuth
  async signInWithGoogle() {
    try {
      if (!isFeatureEnabled('enableCognitoGoogleOAuth')) {
        return { success: false, error: 'Google OAuth is not enabled' }
      }

      await signInWithRedirect({
        provider: 'Google'
      })
      
      return { success: true }
    } catch (error) {
      return { success: false, error: error as AuthError }
    }
  }
}

// Utility functions
export const getAuthError = (error: AuthError): string => {
  if (error.name === 'NotAuthorizedException') {
    return 'Invalid username or password'
  }
  if (error.name === 'UserNotFoundException') {
    return 'User not found'
  }
  if (error.name === 'UsernameExistsException') {
    return 'Username already exists'
  }
  if (error.name === 'InvalidParameterException') {
    return 'Invalid parameters provided'
  }
  if (error.name === 'CodeMismatchException') {
    return 'Invalid verification code'
  }
  if (error.name === 'ExpiredCodeException') {
    return 'Verification code has expired'
  }
  if (error.name === 'LimitExceededException') {
    return 'Too many attempts. Please try again later'
  }
  
  return error.message || 'An authentication error occurred'
}

// Export default initialization
export default initializeCognito
